/**
 * Properties for the selected note (#157) — its text, its box, its type size,
 * and what it points at.
 *
 * The text lives here rather than in an editor floating over the canvas. Every
 * other property in this app is edited in this panel, an on-canvas editor has
 * to track pan and zoom to stay glued to its card, and a note is often parked
 * off to the side where a panel field is the shorter trip anyway. The card on
 * canvas stays a faithful preview — it re-wraps as you type.
 */
import type { Note, Project } from "../model/Project";
// Straight from the module, not the `connections` barrel: importing that
// barrel runs the registry side-effects (and drags paper in with them), which
// a panel section has no business doing — and doing it from here put paper's
// module init ahead of the headless-canvas stub, breaking unrelated model
// tests. The note connection is unregistered anyway; see its header.
import type { ConnectionRowSpec } from "../model/connections/Connection";
import { noteRowSpecs } from "../model/connections/noteConnection";
import {
  MIN_NOTE_SIZE_MM,
  NOTE_PLACEHOLDER,
  noteAnchors,
} from "../model/notes";
import {
  MM_PER_INCH,
  dimensionField,
  fieldNote,
  panelSection,
  scrubberInputField,
} from "./panelFieldBuilders";
import { getUnits } from "../preferences";

export interface NotePanelHost {
  /** Commit a change to the selected note. */
  updateNote: (id: string, patch: Partial<Note>) => void;
  /** Remove it entirely. */
  deleteNote: (id: string) => void;
  /** The height the card is actually DRAWN at — the stored height, or what the
   *  text needs, whichever is greater (#1074). Supplied by the panel host
   *  because measuring the wrap needs a paper scope. */
  cardHeightMm: (note: Note) => number;
  /** One Connections-section row, built by the shared renderer every other
   *  panel uses — so a note's link to its part reads, hovers, cross-highlights
   *  and detaches exactly like a trace or a mirror (#157). */
  buildConnectionRow: (spec: ConnectionRowSpec) => HTMLLIElement;
  project: Project;
}

/** Smallest and largest type a note can be set to, in project mm. Below ~1.5mm
 *  it will not survive being printed; above ~40mm it is a sign, not a note. */
const MIN_FONT_MM = 1.5;
const MAX_FONT_MM = 40;

/**
 * One-shot: the note whose text box should take focus the next time this
 * section renders.
 *
 * Set by the Notes tool when it places a note, so the maker can type straight
 * away instead of dropping a card and then hunting for the field. Consumed on
 * the first render that matches, because the panel re-renders on ordinary
 * events too and stealing focus on every one of them would yank the cursor out
 * of whatever the maker was doing.
 */
let pendingFocusNoteId: string | null = null;

export function focusNoteTextOnNextRender(id: string): void {
  pendingFocusNoteId = id;
}

export function renderNotePanelSection(
  root: HTMLElement,
  note: Note,
  host: NotePanelHost,
): void {
  root.appendChild(panelSection("Note"));

  // --- Text -------------------------------------------------------------
  const area = document.createElement("textarea");
  area.className = "note-text-input";
  area.dataset.role = "note-text";
  area.rows = 4;
  area.value = note.text;
  area.placeholder = NOTE_PLACEHOLDER;
  area.spellcheck = true;
  // Escape steps OUT of the field rather than reaching the tool underneath:
  // while the cursor is in a text box, "back out" means back out of typing.
  // Stopped here so the app's Escape chain doesn't ALSO cancel whatever the
  // canvas is doing — one press, one meaning.
  area.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape") return;
    ev.stopPropagation();
    ev.preventDefault();
    area.blur();
  });
  // Live rather than on blur: the card on canvas is the real preview of how
  // the text wraps into the box, and wrapping is exactly what the maker is
  // deciding while they size the note.
  area.addEventListener("input", () => {
    host.updateNote(note.id, { text: area.value });
  });
  // A note with nothing in it is not a note — it is a blank card sitting on
  // the pattern, printing as an empty box and taking a number on the notes
  // page. Leaving the field without having written anything removes it, which
  // also means a mis-click that placed a card costs nothing to undo. Trimmed,
  // because a space is not writing either.
  area.addEventListener("blur", () => {
    if (area.value.trim().length === 0) host.deleteNote(note.id);
  });
  const textWrap = document.createElement("div");
  textWrap.className = "field";
  const textLabel = document.createElement("label");
  textLabel.textContent = "Text";
  textWrap.appendChild(textLabel);
  textWrap.appendChild(area);
  root.appendChild(textWrap);

  if (pendingFocusNoteId === note.id) {
    pendingFocusNoteId = null;
    // After the element is in the document, or focus() is a no-op.
    queueMicrotask(() => {
      if (!area.isConnected) return;
      area.focus();
      // Cursor at the end of whatever is already written. A new note is empty,
      // so that is the start of an empty box.
      area.setSelectionRange(area.value.length, area.value.length);
    });
  }

  // --- Type size --------------------------------------------------------
  // Sits directly under the text it sizes, because it is a decision about the
  // WRITING — made by eye, against the words already in the box — while Width
  // and Height below are about the card the writing sits on.
  //
  // A scrubber rather than a mm / inch pair: nobody sets a note to "4.5 mm",
  // they drag until it looks right against the drawing, which is a judgment
  // made by eye.
  //
  // It still reads in the maker's own units — the value is stored in mm either
  // way, and only the numbers on the control change. The bounds and step are
  // rounded to something sensible in each system rather than converted
  // literally, which is how 1.5 mm would otherwise show up as 0.0591 in.
  const units = getUnits();
  const inches = units === "in";
  const toDisplay = (mm: number) => (inches ? mm / MM_PER_INCH : mm);
  const fromDisplay = (v: number) => (inches ? v * MM_PER_INCH : v);
  const clampMm = (mm: number) =>
    Math.min(MAX_FONT_MM, Math.max(MIN_FONT_MM, mm));

  const sizeField = scrubberInputField({
    label: "Text size",
    min: inches ? 0.06 : MIN_FONT_MM,
    max: inches ? 1.55 : MAX_FONT_MM,
    // ~0.5 mm either way, so the feel of the drag matches across units.
    step: inches ? 0.02 : 0.5,
    value: toDisplay(note.fontSizeMm),
    suffix: units,
    ariaLabel: `Note text size in ${inches ? "inches" : "millimeters"}`,
    // Live so the card re-sets itself under the drag.
    onLive: (v) =>
      host.updateNote(note.id, { fontSizeMm: clampMm(fromDisplay(v)) }),
    onCommit: (v) =>
      host.updateNote(note.id, { fontSizeMm: clampMm(fromDisplay(v)) }),
  });
  // Wider value box than the shared default: "1.55" plus an "in" suffix and the
  // number spinner do not fit 5.2em, and the digits get clipped. Scoped to this
  // field rather than widening every scrubber in the app.
  sizeField.wrap.classList.add("note-font-field");
  root.appendChild(sizeField.wrap);

  // --- Box --------------------------------------------------------------
  root.appendChild(
    dimensionField("Width", note.widthMm, (v) =>
      host.updateNote(note.id, { widthMm: Math.max(MIN_NOTE_SIZE_MM, v) }),
    ),
  );
  root.appendChild(
    dimensionField("Height", note.heightMm, (v) =>
      host.updateNote(note.id, { heightMm: Math.max(MIN_NOTE_SIZE_MM, v) }),
    ),
  );
  // Say so when the card on the canvas is taller than the number in the field
  // (#1074). Height is a FLOOR, not a cap: a note whose words are cut off is
  // worse than useless, so the text wins and the box grows. Without this line
  // the field looks like it is being ignored.
  const grown = host.cardHeightMm(note);
  if (grown > note.heightMm + 0.05) {
    const grownNote = fieldNote(
      "Grown to fit the text. Widen the box, or set a smaller type size, to bring it back down.",
    );
    grownNote.dataset.role = "note-grown";
    root.appendChild(grownNote);
  }
  // --- What it points at ------------------------------------------------
  //
  // A live Connections row when the link is real — clickable through to the
  // part, cross-highlighting with the chips in the parts list, and detachable
  // with the × — and a plain sentence only for the two cases that have no far
  // side to link to.
  const rows = noteRowSpecs(host.project, note);
  if (rows.length > 0) {
    const section = panelSection("Connections");
    const list = document.createElement("ul");
    list.className = "constraints-list";
    for (const spec of rows) list.appendChild(host.buildConnectionRow(spec));
    section.appendChild(list);
    section.dataset.role = "note-target";
    root.appendChild(section);
  } else {
    const target = fieldNote(
      noteAnchors(note).length > 0
        ? // The parts are gone but the note is kept — say so rather than
          // letting a callout that resolves to nothing look like a bug.
          "What this pointed at was deleted. The note is kept."
        : "Not attached to anything. Drag from the numbered badge onto a part, a cutout or a seam to point at it.",
    );
    target.dataset.role = "note-target";
    root.appendChild(target);
  }

  const del = document.createElement("button");
  del.className = "btn btn-danger";
  del.dataset.role = "note-delete";
  del.textContent = "Delete note";
  del.addEventListener("click", () => host.deleteNote(note.id));
  root.appendChild(del);
}
